import React from 'react';
import { Link } from 'react-router-dom';
import { FiCalendar, FiChevronRight, FiAlertCircle } from 'react-icons/fi';

const StudentCard = ({ student }) => {
  // Only present on the "All Students" view — students fetched from the
  // plain survey-results list have always completed one, by definition.
  const surveyCompleted = student.surveyCompleted !== false;

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Handle both old format (surveyMetadata.completedAt) and new format (completedAt)
  const getCompletedDate = () => {
    if (student.completedAt) {
      return student.completedAt;
    }
    if (student.surveyMetadata?.completedAt) {
      return student.surveyMetadata.completedAt;
    }
    if (student.createdAt) {
      return student.createdAt;
    }
    return new Date().toISOString();
  };

  // Get student name for display
  const getStudentName = () => {
    if (student.studentInfo) {
      const { firstName, lastName } = student.studentInfo;
      if (firstName || lastName) {
        return `${firstName || ''} ${lastName || ''}`.trim();
      }
    }
    return null;
  };

  const studentName = getStudentName();

  const cardContent = (
    <>
      <div className="flex justify-between items-start mb-3">
        <div>
          {studentName && (
            <h3 className="text-lg font-semibold text-gray-900 mb-0.5">
              {studentName}
            </h3>
          )}
          <p className={`${studentName ? 'text-sm text-gray-600' : 'text-lg font-semibold text-gray-900'} mb-1`}>
            ID: {student.studentID}
          </p>
          {surveyCompleted ? (
            <div className="flex items-center text-sm text-gray-500">
              <FiCalendar className="mr-1" />
              {formatDate(getCompletedDate())}
            </div>
          ) : (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">
              <FiAlertCircle className="mr-1 h-3 w-3" />
              Survey Not Completed
            </span>
          )}
        </div>
        {surveyCompleted && <FiChevronRight className="text-gray-400" />}
      </div>

      {surveyCompleted && (
        <div className="space-y-2">
          <p className="text-sm text-gray-600 font-medium">Recommended Clusters:</p>
          <div className="flex flex-wrap gap-2">
            {student.recommendedClusters && student.recommendedClusters.length > 0 ? (
              student.recommendedClusters.map((cluster, index) => {
                // Handle both object format {careerCluster: "..."} and string format
                const clusterName = typeof cluster === 'object' ? cluster.careerCluster : cluster;
                return (
                  <span
                    key={index}
                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800"
                  >
                    {clusterName}
                  </span>
                );
              })
            ) : (
              <span className="text-sm text-gray-500 italic">No clusters selected</span>
            )}
          </div>
        </div>
      )}
    </>
  );

  if (!surveyCompleted) {
    return (
      <div className="block bg-white rounded-lg shadow-sm border border-amber-200 p-5">
        {cardContent}
      </div>
    );
  }

  return (
    <Link
      to={`/student/${student.studentID}`}
      className="block bg-white rounded-lg shadow-sm border border-gray-200 p-5 hover:shadow-md hover:border-primary-300 transition-all"
    >
      {cardContent}
    </Link>
  );
};

export default StudentCard;
